import { useEffect, useRef, useState, useCallback } from 'react';
import useSpeechApi from './useSpeechApi';

type ConnectionStatus = 'Connecting' | 'Connected' | 'Disconnected';

const useWebSocket = (url: string | null) => {
  const [lastMessage, setLastMessage] = useState<MessageEvent | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('Connecting');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const maxReconnectAttempts = 5;
  const reconnectAttemptsRef = useRef(0);
  const { refreshPresignedUrl } = useSpeechApi();

  // Function to refresh pre-signed URLs in messages
  const refreshAudioUrl = useCallback(async (s3Key: string): Promise<string | null> => {
    if (!s3Key) {
      console.error('Cannot refresh URL: no S3 key provided');
      return null;
    }
    
    try {
      const result = await refreshPresignedUrl(s3Key);
      if ('presigned_url' in result) {
        return result.presigned_url;
      } else {
        console.error('Failed to refresh URL:', result.error);
        return null;
      }
    } catch (error) {
      console.error('Error refreshing URL:', error);
      return null;
    }
  }, [refreshPresignedUrl]);

  const connect = useCallback(() => {
    try {
      // Don't connect if URL is null
      if (!url) {
        console.log('WebSocket URL is null, not connecting');
        setConnectionStatus('Disconnected');
        return;
      }

      // Clear existing connection
      if (wsRef.current) {
        console.log('Closing existing WebSocket connection');
        wsRef.current.close();
        wsRef.current = null;
      }

      console.log('Attempting to connect to WebSocket:', url);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket Connected successfully');
        setConnectionStatus('Connected');
        reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };

      ws.onmessage = (event) => {
        console.log('Received WebSocket message:', event.data);
        setLastMessage(event);
      };

      ws.onclose = (event) => {
        console.log('WebSocket Disconnected', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        setConnectionStatus('Disconnected');
        wsRef.current = null;

        // Only attempt to reconnect if we haven't exceeded max attempts and have a valid URL
        if (url && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          console.log(`Attempting to reconnect (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
          reconnectTimeoutRef.current = setTimeout(connect, 2000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket Error:', error);
        if (wsRef.current) {
          wsRef.current.close();
        }
      };
    } catch (error) {
      console.error('WebSocket Connection Error:', error);
      setConnectionStatus('Disconnected');
      
      // Only attempt to reconnect if we haven't exceeded max attempts and have a valid URL
      if (url && reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current += 1;
        console.log(`Attempting to reconnect (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
        reconnectTimeoutRef.current = setTimeout(connect, 2000);
      }
    }
  }, [url]);

  // Initial connection and URL change handling
  useEffect(() => {
    // Reset connection status when URL changes
    setConnectionStatus('Connecting');
    reconnectAttemptsRef.current = 0;
    connect();
    
    // Cleanup function
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, url]);

  // Add a ping mechanism to keep the connection alive
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Send ping every 30 seconds

    return () => clearInterval(pingInterval);
  }, []);

  const sendMessage = useCallback((message: string) => {
    if (!url) {
      console.log('No WebSocket URL provided');
      return;
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.log('WebSocket not connected, attempting to reconnect...');
      setConnectionStatus('Disconnected');
      connect(); // Try to reconnect
      return;
    }
    
    try {
      wsRef.current.send(message);
    } catch (error) {
      console.error('Error sending message:', error);
      setConnectionStatus('Disconnected');
      connect(); // Try to reconnect
    }
  }, [connect, url]);

  return { lastMessage, connectionStatus, sendMessage, refreshAudioUrl };
};

export default useWebSocket; 